import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { ClienteCard } from '@/components/dashboard/ClienteCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'react-hot-toast';
import { cn } from '@/lib/utils';

interface ClienteConDocumentos {
  id: string;
  razon_social: string;
  nombre_comercial?: string;
  docs_pendientes: number;
  docs_procesados_ia: number;
  ultimo_documento?: string;
  ultimo_estado?: string;
}

export default function DashboardContabilizacion() {
  const { user } = useAuth();
  const router = useRouter();
  const [clientes, setClientes] = useState<ClienteConDocumentos[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'todos' | 'pendientes' | 'con_ia'>('todos');

  // Cargar clientes con documentos pendientes
  useEffect(() => {
    cargarClientes();
  }, []);

  const cargarClientes = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/clientes/con-documentos-pendientes');
      if (!response.ok) throw new Error('Error al cargar clientes');
      const data = await response.json();
      setClientes(data.data || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  // Navegar a trabajar con cliente específico
  const handleTrabajar = (clienteId: string) => {
    router.push(`/asesor/documentos-cliente?clienteId=${clienteId}`);
  };

  // Filtrar clientes
  const clientesFiltrados = clientes.filter((cliente) => {
    // Filtro de búsqueda
    const matchesSearch =
      cliente.razon_social.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cliente.nombre_comercial?.toLowerCase().includes(searchTerm.toLowerCase());

    // Filtro de tipo
    const totalDocs = cliente.docs_pendientes + cliente.docs_procesados_ia;
    if (filter === 'pendientes') {
      return matchesSearch && cliente.docs_pendientes > 0;
    }
    if (filter === 'con_ia') {
      return matchesSearch && cliente.docs_procesados_ia > 0;
    }
    return matchesSearch && totalDocs > 0;
  });

  // Estadísticas
  const stats = {
    totalClientes: clientes.length,
    totalPendientes: clientes.reduce((sum, c) => sum + c.docs_pendientes, 0),
    totalConIA: clientes.reduce((sum, c) => sum + c.docs_procesados_ia, 0),
  };

  return (
    <Layout rol="asesor">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-light text-gray-900">Contabilización</h1>
            <p className="text-gray-500 mt-1">
              Gestiona los documentos pendientes por cliente
            </p>
          </div>

          {/* Estadísticas */}
          <div className="flex gap-3">
            <Badge variant="outline" className="px-4 py-2 text-base">
              <span className="font-semibold">{stats.totalClientes}</span> clientes
            </Badge>
            <Badge variant="default" className="px-4 py-2 text-base bg-blue-100 text-blue-800 border-blue-300">
              <span className="font-semibold">{stats.totalPendientes}</span> pendientes
            </Badge>
            <Badge variant="secondary" className="px-4 py-2 text-base">
              <span className="font-semibold">{stats.totalConIA}</span> con IA
            </Badge>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Input
            placeholder="Buscar cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />

          <div className="flex gap-2">
            {(['todos', 'pendientes', 'con_ia'] as const).map((f) => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f)}
              >
                {f === 'todos' && 'Todos'}
                {f === 'pendientes' && 'Pendientes'}
                {f === 'con_ia' && 'Con IA'}
              </Button>
            ))}
          </div>
        </div>

        {/* Grid de clientes */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loading
            ? Array.from({ length: 8 }).map((_, i) => (
                <ClienteCard
                  key={i}
                  cliente={{} as any}
                  onTrabajar={() => {}}
                  isLoading
                />
              ))
            : clientesFiltrados.map((cliente) => (
                <ClienteCard
                  key={cliente.id}
                  cliente={cliente}
                  onTrabajar={handleTrabajar}
                />
              ))}
        </div>

        {/* Empty state */}
        {!loading && clientesFiltrados.length === 0 && (
          <div className="text-center py-12">
            <span className="material-symbols-outlined text-6xl text-gray-300">account_balance</span>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No hay documentos pendientes</h3>
            <p className="mt-1 text-gray-500">
              Todos los documentos han sido contabilizados
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
